import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { GOOGLE_BUSINESS_CONNECTION_STATE } from "@/business/constants";
import { useGetGoogleBusinessAccountLocations } from "@/business/google-business-query";
import { useGetServiceTitanCredentials } from "@/business/service-titan-query";
import useBusiness from "@/core/business/use-business";
import { useGetAccountSummary } from "@/internal-transactions/query";
import { useGetTechnicians } from "@/user/query";
import { GoogleBusinessPostOnboardingModal } from "./google-business-post-onboarding-modal";
import { OnboardingActionButton } from "./onboarding-action-button";
import PaymentModal from "./payment-modal";
import { ServiceTitanIntegrationModal } from "./service-titan-integration-modal";
import { TechnicianUploadModal } from "./technician-upload-modal";

interface PostOnboardingItemsProps {
  className?: string;
}

const PostOnboardingItems = ({ className }: PostOnboardingItemsProps) => {
  const { selectedBusiness } = useBusiness();
  const {
    id: businessId,
    dots_complete,
    is_spiff_only: isSpiffOnly,
  } = selectedBusiness || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [technicianModalOpen, setTechnicianModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [serviceTitanModalOpen, setServiceTitanModalOpen] = useState(false);
  const [googleBusinessModalOpen, setGoogleBusinessModalOpen] = useState(false);

  const { data: technicians = [] } = useGetTechnicians(businessId);
  const { data: accountSummary } = useGetAccountSummary(businessId);
  const { data: serviceTitanCredentials } =
    useGetServiceTitanCredentials(businessId);
  const { data: googleBusinessLocations = [] } =
    useGetGoogleBusinessAccountLocations(businessId);

  const hasTechnicians = technicians.length > 0;
  const hasFunds = accountSummary && Number(accountSummary.wallet_balance) > 0;
  const hasServiceTitanIntegration =
    serviceTitanCredentials?.credentials_status === "connection_established";
  const hasGoogleBusinessConnection =
    selectedBusiness?.google_business_connection_state ===
      GOOGLE_BUSINESS_CONNECTION_STATE.connected &&
    googleBusinessLocations.length > 0;

  const handleTechnicianClick = () => {
    if (!hasTechnicians) {
      setTechnicianModalOpen(true);
    }
  };

  const handlePaymentClick = () => {
    if (!hasFunds) {
      setPaymentModalOpen(true);
    }
  };

  const handleServiceTitanClick = () => {
    if (!hasServiceTitanIntegration) {
      setServiceTitanModalOpen(true);
    }
  };

  const handleGoogleBusinessClick = () => {
    if (!hasGoogleBusinessConnection) {
      setGoogleBusinessModalOpen(true);
    }
  };

  const handleTechnicianComplete = () => {
    setTechnicianModalOpen(false);
  };

  const handlePaymentComplete = () => {
    setPaymentModalOpen(false);
  };

  useEffect(() => {
    const googleBusinessModal = searchParams.get("google_business_modal");
    if (googleBusinessModal === "open") {
      setGoogleBusinessModalOpen(true);
      searchParams.delete("google_business_modal");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <Card
        className={`w-full max-w-md rounded-2xl border border-stroke-tertiary bg-card p-6 shadow-sm ${className}`}
      >
        <Stack gap="md" fullWidth>
          <Typography variant="subtitleW1" textColor="tabSelected">
            Congrats on setting up Volca!
          </Typography>

          <Typography variant="subtitleW3" className="text-secondary-text">
            While we wait for your referrals to come in, we have a couple more
            tasks for you
          </Typography>

          <Stack gap="none" fullWidth>
            {!isSpiffOnly && (
              <OnboardingActionButton
                text="Upload a list of your technicians"
                isCompleted={hasTechnicians}
                onClick={handleTechnicianClick}
              />
            )}
            {!isSpiffOnly && dots_complete && (
              <OnboardingActionButton
                text="Deposit your first funds"
                isCompleted={hasFunds}
                onClick={handlePaymentClick}
                checkIconColor="text-green-500"
              />
            )}
            <OnboardingActionButton
              text="Set up Service Titan integration"
              isCompleted={hasServiceTitanIntegration}
              onClick={handleServiceTitanClick}
              checkIconColor="text-blue-500"
            />
            <OnboardingActionButton
              text="Connect your Google Business account"
              isCompleted={hasGoogleBusinessConnection}
              onClick={handleGoogleBusinessClick}
              checkIconColor="text-purple-500"
            />
          </Stack>
        </Stack>
      </Card>

      <TechnicianUploadModal
        isOpen={technicianModalOpen}
        onOpenChange={setTechnicianModalOpen}
        onComplete={handleTechnicianComplete}
        businessId={businessId}
      />

      <PaymentModal
        isOpen={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        onComplete={handlePaymentComplete}
        businessId={businessId}
      />

      <ServiceTitanIntegrationModal
        isOpen={serviceTitanModalOpen}
        onOpenChange={setServiceTitanModalOpen}
        businessId={businessId}
      />

      <GoogleBusinessPostOnboardingModal
        isOpen={googleBusinessModalOpen}
        onOpenChange={setGoogleBusinessModalOpen}
        businessId={businessId}
        business={selectedBusiness}
      />
    </>
  );
};

export default PostOnboardingItems;
